import { create } from 'zustand';

export type LiveNextShowMode = 'mediaOnly' | 'both' | 'speakerOnly';

/**
 * Overlay may hold an item id or a `item|slot|scope` cue identity.
 * Sync / resolve must compare the item id only — a first-click cue-identity
 * hold must not look like a Next advance.
 */
export function liveNextItemIdFromHoldKey(holdKey: string | undefined | null): string | undefined {
  if (!holdKey) {
    return undefined;
  }
  const itemId = holdKey.split('|')[0];
  return itemId || undefined;
}

type FlowControlLiveNextShowModeState = {
  /**
   * Next item id the operator override applies to.
   * Cleared when Control Next identity changes (cue advance).
   */
  heldForNextItemId: string | null;
  showMode: LiveNextShowMode | null;
  hold: (nextItemId: string, showMode: LiveNextShowMode) => void;
  /** Drop hold when Next moves to a different item. */
  syncNextItemId: (nextItemId: string | undefined) => void;
  clear: () => void;
};

/**
 * LIVE Control: operator Next show-mode override that must survive perform-control WS /
 * GET hydrates (those rebuild `cueState.lookAheadShowMode` from item presets).
 * BE persist still happens via staged-selection; this store is the UI source of truth
 * until Next identity changes — same idea as simulation `holdNextShowModePreselect`.
 */
export const useFlowControlLiveNextShowModeStore = create<FlowControlLiveNextShowModeState>((set, get) => ({
  heldForNextItemId: null,
  showMode: null,
  hold: (nextItemId, showMode) => set({ heldForNextItemId: nextItemId, showMode }),
  syncNextItemId: (nextItemId) => {
    const { heldForNextItemId: held, showMode } = get();
    // Ignore transient undefined during perform-control WS gaps — only clear when Next
    // identity is known and actually moved (cue advance).
    if (held == null || nextItemId == null) {
      return;
    }
    if (liveNextItemIdFromHoldKey(held) === nextItemId) {
      if (held !== nextItemId) {
        set({ heldForNextItemId: nextItemId, showMode });
      }
      return;
    }
    set({ heldForNextItemId: null, showMode: null });
  },
  clear: () => set({ heldForNextItemId: null, showMode: null }),
}));

/** Held override for the current Next item, if any. */
export function resolveHeldLiveNextShowMode(nextItemId: string | undefined): LiveNextShowMode | undefined {
  if (!nextItemId) {
    return undefined;
  }
  const { heldForNextItemId, showMode } = useFlowControlLiveNextShowModeStore.getState();
  if (liveNextItemIdFromHoldKey(heldForNextItemId) !== nextItemId || showMode == null) {
    return undefined;
  }
  return showMode;
}
